import type { KeywordContent, PageData, ServiceDefinition } from "./types";
import { allServices } from "./services";
import { junkRemoval } from "./services/en/junk-removal";

// ---------------------------------------------------------------------------
// Interpolation: replace {{kommun}} / {{kommunNamn}} in all string fields
// ---------------------------------------------------------------------------

function interpolate<T>(obj: T, replacements: Record<string, string>): T {
  if (typeof obj === "string") {
    let result: string = obj;
    for (const [key, value] of Object.entries(replacements)) {
      result = result.replaceAll(`{{${key}}}`, value);
    }
    // Clean up double/triple spaces from empty replacements
    result = result.replace(/\s{2,}/g, " ").trim();
    return result as T;
  }
  if (Array.isArray(obj)) {
    return obj.map((item) => interpolate(item, replacements)) as T;
  }
  if (typeof obj === "object" && obj !== null) {
    const result: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(obj)) {
      result[key] = interpolate(value, replacements);
    }
    return result as T;
  }
  return obj;
}

// ---------------------------------------------------------------------------
// Swedish resolver — exact keyword match only (no municipality variants)
// ---------------------------------------------------------------------------

const svKeywordMap = new Map<
  string,
  { service: ServiceDefinition; keyword: KeywordContent }
>();

for (const service of allServices) {
  for (const keyword of service.keywords) {
    svKeywordMap.set(keyword.slug, { service, keyword });
  }
}

export function resolveSlug(slug: string): PageData | null {
  const entry = svKeywordMap.get(slug);
  if (!entry) return null;

  return {
    slug,
    service: entry.service,
    keyword: interpolate(entry.keyword, { kommun: "", kommunNamn: "" }),
    municipality: null,
  };
}

export function getAllSlugs(): string[] {
  return [...svKeywordMap.keys()];
}

// ---------------------------------------------------------------------------
// English resolver
// ---------------------------------------------------------------------------

const enServices: ServiceDefinition[] = [
  {
    id: "junk-removal",
    name: "Junk Removal",
    keywords: [junkRemoval],
  },
];

const enKeywordMap = new Map<
  string,
  { service: ServiceDefinition; keyword: KeywordContent }
>();

for (const service of enServices) {
  for (const keyword of service.keywords) {
    enKeywordMap.set(keyword.slug, { service, keyword });
  }
}

export function resolveEnSlug(slug: string): PageData | null {
  const entry = enKeywordMap.get(slug);
  if (!entry) return null;

  return {
    slug,
    service: entry.service,
    keyword: interpolate(entry.keyword, { kommun: "", kommunNamn: "" }),
    municipality: null,
  };
}

export function getAllEnSlugs(): string[] {
  return [...enKeywordMap.keys()];
}

// ---------------------------------------------------------------------------
// Language mapping: sv slug ↔ en slug
// ---------------------------------------------------------------------------

const svToEnKeyword: Record<string, string> = {
  bortforsling: "junk-removal",
};

const enToSvKeyword: Record<string, string> = {
  "junk-removal": "bortforsling",
};

// Sort longest-first so "bortforsling-mobler" matches before "bortforsling"
const sortedSvToEn = Object.entries(svToEnKeyword).sort(
  ([a], [b]) => b.length - a.length,
);
const sortedEnToSv = Object.entries(enToSvKeyword).sort(
  ([a], [b]) => b.length - a.length,
);

/** Given a Swedish slug, return the matching English slug (or null). */
export function getEnSlug(svSlug: string): string | null {
  for (const [svKw, enKw] of sortedSvToEn) {
    if (svSlug === svKw) return enKw;
    if (svSlug.startsWith(svKw + "-")) {
      return enKw + svSlug.slice(svKw.length);
    }
  }
  return null;
}

/** Given an English slug, return the matching Swedish slug (or null). */
export function getSvSlug(enSlug: string): string | null {
  for (const [enKw, svKw] of sortedEnToSv) {
    if (enSlug === enKw) return svKw;
    if (enSlug.startsWith(enKw + "-")) {
      return svKw + enSlug.slice(enKw.length);
    }
  }
  return null;
}
